/**
 * When a staged app update may actually apply.
 *
 * Specification: ../test/update-gate.spec.ts. Master plan §3 K8.
 *
 * The plan does not hedge on this: "The PWA service worker never auto-activates
 * mid-shift. `skipWaiting()` in a POS is how you lose an order."
 *
 * The failure is specific and expensive. A build lands, the worker activates,
 * every open tab reloads, and the server holding a handheld mid-order loses the
 * check they were building — simultaneously, on every device in the venue,
 * because they all poll the same endpoint.
 *
 * So this is the whole feature. The service worker around it is plumbing: it
 * installs, it waits, and it asks here before it does anything.
 */

export interface QuietWindow {
  /** Local hour, inclusive. */
  readonly fromHour: number;
  /** Local hour, exclusive. Less than `fromHour` wraps past midnight. */
  readonly toHour: number;
}

export interface UpdateGateOptions {
  /** How long a device must be untouched to count as idle. */
  readonly idleThresholdMs: number;
  /**
   * Windows when an update is welcome even on a busy-looking device.
   *
   * Worth aligning with the Android Management API system-update window
   * (03:00–05:00 local): one maintenance window per venue, not two that fight.
   */
  readonly quietHours: readonly QuietWindow[];
}

export interface UpdateCondition {
  readonly hasStagedUpdate: boolean;
  readonly openCheckCount: number;
  readonly outboxDepth: number;
  readonly shiftOpen: boolean;
  /** Since the last interaction. */
  readonly idleMs: number;
  readonly now: number;
  readonly managerRequested: boolean;
  /** The venue's zone. Defaults to UTC only because a test may omit it. */
  readonly timeZone?: string;
}

export type UpdateReason =
  | "NOTHING_STAGED"
  | "CHECKS_OPEN"
  | "OUTBOX_NOT_DRAINED"
  | "NOT_IDLE"
  | "IDLE"
  | "SHIFT_CLOSED"
  | "MANAGER_REQUESTED"
  | "QUIET_HOURS";

export interface UpdateDecision {
  readonly apply: boolean;
  readonly reason: UpdateReason;
  /** True when something is waiting, whether or not it may apply yet. */
  readonly staged: boolean;
  /** Announced to staff, so a later reload is not a surprise. */
  readonly messageKey: string | null;
}

function localHour(now: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    hour12: false,
  }).formatToParts(new Date(now));
  const hour = parts.find((p) => p.type === "hour")?.value ?? "0";
  return Number(hour) % 24;
}

const inWindow = (hour: number, window: QuietWindow): boolean =>
  window.fromHour <= window.toHour
    ? hour >= window.fromHour && hour < window.toHour
    : // Wraps midnight: 23:00–04:00 is an ordinary window for a late bar.
      hour >= window.fromHour || hour < window.toHour;

export class UpdateGate {
  readonly #options: UpdateGateOptions;

  constructor(options: UpdateGateOptions) {
    this.#options = options;
  }

  evaluate(condition: UpdateCondition): UpdateDecision {
    const staged = condition.hasStagedUpdate;
    const messageKey = staged ? "pos.update.staged" : null;

    const decide = (apply: boolean, reason: UpdateReason): UpdateDecision => ({
      apply,
      reason,
      staged,
      messageKey,
    });

    if (!staged) return decide(false, "NOTHING_STAGED");

    // THE SAFETY CHECKS COME FIRST, and nothing overrides them — not a manager
    // tap, not a quiet-hours window. A manager pressing "update now" does not
    // know that a server two rooms away is mid-order, and the person who loses
    // the check is not the person who pressed the button.
    if (condition.openCheckCount > 0) return decide(false, "CHECKS_OPEN");

    // A durable outbox means nothing is lost by reloading — but a till that
    // reboots holding four unsent tenders is a till whose staff stop trusting
    // it, and trust is the thing that is expensive to get back.
    if (condition.outboxDepth > 0) return decide(false, "OUTBOX_NOT_DRAINED");

    // Now the reasons TO apply, in order of how deliberate they are.
    if (condition.managerRequested) return decide(true, "MANAGER_REQUESTED");

    // The Z-report: the shift is closed, the drawer is counted, the books are
    // done. The natural boundary, and the one the plan names first.
    if (!condition.shiftOpen) return decide(true, "SHIFT_CLOSED");

    const zone = condition.timeZone ?? "UTC";
    if (this.#options.quietHours.some((w) => inWindow(localHour(condition.now, zone), w))) {
      // Evaluated in the VENUE's zone. A fleet across several zones updating on
      // UTC would wake half of them mid-service.
      return decide(true, "QUIET_HOURS");
    }

    if (condition.idleMs >= this.#options.idleThresholdMs) return decide(true, "IDLE");

    // A server reading a ticket is not an idle device.
    return decide(false, "NOT_IDLE");
  }
}
